import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../api/client';
import { Lock, Mail, ArrowRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import PasswordInput from '../components/PasswordInput';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setErr('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      const to = location.state?.from?.pathname || '/';
      navigate(to, { replace: true });
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail, e?.response?.data) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col p-8 md:p-16 justify-between" data-testid="login-left">
        <div className="flex items-center gap-2">
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="Unikin"
            className="w-9 h-9 object-contain"
          />
          <div>
            <div className="heading font-black text-ink lowercase">unikin</div>
            <div className="overline text-[10px]">Ops Control System</div>
          </div>
        </div>

        <div className="max-w-md w-full mt-16">
          <div className="overline mb-3">Authentication · Step 1/1</div>
          <h1 className="heading text-4xl md:text-5xl font-black text-ink tracking-tight">
            Sign in to<br /><span className="text-primary">control the gate.</span>
          </h1>
          <p className="text-zinc-600 mt-4 text-sm">
            Issue passes, verify QR codes, track visitors and review approvals — all from one industrial console.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="label">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  className="input pl-9"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <label className="label">Password</label>
              <PasswordInput
                className="input pl-9"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
              >
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              </PasswordInput>
            </div>
            {err && (
              <div className="flex items-start gap-2 text-red-700 bg-red-50 border border-red-200 px-3 py-2 text-sm rounded-sm" data-testid="login-error">
                <AlertCircle size={16} className="mt-0.5" />
                <span>{err}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading ? 'Signing in…' : 'Sign In'} <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-6 text-xs text-zinc-500">
            Candidate? <Link to="/apply" className="text-primary font-semibold hover:underline" data-testid="link-apply">Fill the employment form →</Link>
          </div>
        </div>

        <div className="hidden md:block text-xs text-zinc-400">
          &copy; {new Date().getFullYear()} Unikin · v1.0
        </div>
      </div>

      {/* Right: industrial visual */}
      <div className="hidden md:block relative bg-ink noise">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1654703680126-7cbf0f11a40b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZmFjdG9yeSUyMHdvcmtlcnxlbnwwfHx8fDE3NzY3MzI0MzR8MA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-ink/70 via-ink/40 to-primary/30" />
        <div className="relative h-full flex flex-col justify-between p-16 text-white">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full pulse-dot" />
            <span className="overline text-white/70">Live · All systems operational</span>
          </div>
          <div>
            <div className="heading font-black text-5xl tracking-tighter leading-[0.95] mb-4">
              Paperless<br /> gate control<br /> for modern<br /> factories.
            </div>
            <div className="flex gap-6 mt-10 text-sm">
              <div>
                <div className="heading text-3xl font-black text-primary">5</div>
                <div className="overline text-white/60">Roles</div>
              </div>
              <div>
                <div className="heading text-3xl font-black text-primary">QR</div>
                <div className="overline text-white/60">Verified</div>
              </div>
              <div>
                <div className="heading text-3xl font-black text-primary">24/7</div>
                <div className="overline text-white/60">Audit Trail</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-px bg-white/10">
            {['Visitors','Gate Pass','Items','Candidates','Monthly','Reports'].map((t) => (
              <div key={t} className="bg-ink/80 backdrop-blur px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-white/80">
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
